import { ProjectTreeNodeT } from "../hooks/project-layer-tree/useProjectLayerTree.types";

const isLayerNode = (node: ProjectTreeNodeT): boolean =>
  node?.type === "layer" || node?.type === "shapefile";

const isFolderNode = (node: ProjectTreeNodeT): boolean =>
  node?.type === "folder" || node?.type === "node";

/**
 * True for a folder the bulk delete only unlinks. getManageSelectionState routes
 * these to orgFolderIds/systemFolderIds, which the service removes from the
 * project rather than deleting - so nothing inside them is going away.
 */
const isUnlinkOnlyFolder = (node: ProjectTreeNodeT): boolean => {
  const source = node?.source ?? node?.folder?.source;
  return source === "org" || source === "mcdan";
};

/**
 * True for a layer the bulk delete only unlinks: a project-linked shapefile,
 * which getManageSelectionState routes to projectShapefileIds. The tree sets
 * both shapefileId and projectShapefileId on such a node (useProjectLayerTree),
 * so the presence of the link id - not the absence of the shapefile id - is what
 * distinguishes an unlink from a delete.
 */
const isUnlinkOnlyLayer = (node: ProjectTreeNodeT): boolean => !!node?.projectShapefileId;

const collectSubtreeIds = (node: ProjectTreeNodeT, out: Set<string>): void => {
  if (isFolderNode(node) && isUnlinkOnlyFolder(node)) return;

  if (isLayerNode(node) && !isUnlinkOnlyLayer(node) && node.shapefileId) {
    out.add(node.shapefileId);
  }

  node?.children?.forEach((child) => collectSubtreeIds(child, out));
};

/**
 * The shapefile ids a checked selection will genuinely delete, folders expanded.
 *
 * Two things make this necessary rather than readable off the delete payload.
 * getManageSelectionState collapses a fully-checked folder down to its own id
 * and drops the child shapefile ids - right for the payload, but it leaves the
 * mutation's variables unable to say which shapefiles went, so anything keyed
 * off them (purging deleted shapefiles' fields from user defaults, EVA-126)
 * would miss every shapefile deleted by way of its folder. And the payload
 * mixes deletes with unlinks: projectShapefileIds, orgFolderIds and
 * systemFolderIds are only removed from the project, and the shapefiles behind
 * them survive to be re-linked.
 *
 * So this mirrors getManageSelectionState's classification and reports only the
 * genuine deletes. Over-reporting would mean silently rewriting a user's saved
 * defaults for a shapefile that still exists.
 *
 * A checked node takes its whole subtree with it. antd's cascading selection
 * puts only fully-checked nodes in checkedKeys - a partially-checked folder
 * lands in halfCheckedKeys instead - so recursing past unchecked nodes and
 * sweeping the subtree of checked ones covers both shapes without
 * double-counting.
 */
export const collectCheckedShapefileIds = (
  nodes: Maybe<ProjectTreeNodeT[]>,
  checkedKeys: Maybe<string[]>
): string[] => {
  const checked = new Set(checkedKeys ?? []);
  if (!checked.size) return [];

  const ids = new Set<string>();

  const walk = (node: ProjectTreeNodeT): void => {
    if (checked.has(node?.key)) {
      collectSubtreeIds(node, ids);
      return;
    }
    node?.children?.forEach(walk);
  };

  (nodes ?? []).forEach(walk);
  return [...ids];
};

/**
 * The structural minimum a folder-delete needs to know about its subtree. Two
 * different node shapes flow into collectFolderShapefileIds - ProjectTreeNodeT
 * from the project layer tree and IShapefileNode from the org-settings trees -
 * and both satisfy this.
 */
export interface DeletableFolderNode {
  shapefileId?: string;
  projectShapefileId?: string;
  children?: DeletableFolderNode[];
}

/**
 * The shapefile ids that genuinely die when this folder is deleted outright
 * (useDeleteProjectShapefileFolderMutation cascade-deletes the subtree).
 *
 * A node carrying projectShapefileId is a project *link* to an org/system
 * shapefile: the cascade removes the link and the shapefile survives to be
 * re-linked, so reporting it deleted would rewrite a user's saved defaults for
 * a shapefile that still exists - the same rule collectCheckedShapefileIds
 * applies above.
 *
 * Unlike collectCheckedShapefileIds this deliberately has no source-based
 * folder skip: the delete action is only offered on folders that genuinely die
 * (project-owned folders in the layer tree; org/system folders in org
 * settings), and in the org-settings trees a source of "org" means owned
 * there, not linked - skipping on it would silently miss every org shapefile.
 */
export const collectFolderShapefileIds = (
  folderNode: Maybe<DeletableFolderNode>
): string[] => {
  const ids = new Set<string>();

  const walk = (node: DeletableFolderNode): void => {
    if (node?.shapefileId && !node.projectShapefileId) {
      ids.add(node.shapefileId);
    }
    node?.children?.forEach(walk);
  };

  if (folderNode) walk(folderNode);
  return [...ids];
};
